import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { Color } from 'style/types';

const transparentBlocks = css`
  position: relative;
  height: 100%;
  padding: var(--half-unit) var(--unit) var(--half-unit) var(--clip-path-delta);
  &:after {
    position: absolute;
    top: -1px;
    bottom: -1px;
    width: var(--clip-path-delta);
    background: var(--color);
    content: '';
  }
`;

const Root = styled.div`
  isolation: isolate;
  position: relative;
`;
const MissionWrapper = styled.div`
  --clip-path-delta: var(--double-unit);
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: stretch;
  padding-left: var(--intermediate-unit);
`;

const InfoWrapper = styled(Tooltip)`
  display: block;
`;
const Infos = styled(Vertical)`
  ${transparentBlocks}
  padding-right: calc(var(--clip-path-delta) + var(--half-unit));
  padding-top: var(--intermediate-unit);
  padding-bottom: var(--intermediate-unit);
  padding-left: var(--double-and-a-half-unit);
  justify-content: center;
  clip-path: polygon(
    0 0,
    0 100%,
    100% 100%,
    calc(100% - var(--clip-path-delta)) 0
  );
  background: var(--c-black)
    linear-gradient(to right, var(--c-black) 10%, var(--bg-color) 100%);
  border: 1px solid;
  border-left: 0;
  border-image-slice: 1;
  border-image-source: linear-gradient(to right, transparent, var(--color));
  &:after {
    position: absolute;
    z-index: -1;
    left: 100%;
    clip-path: polygon(0 0, 100% 0, 100% 100%, var(--clip-path-delta) 100%);
    transform: translateX(-100%);
  }
  &.claimable:hover {
    cursor: pointer;
    &:before {
      position: absolute;
      z-index: -1;
      inset: 0;
      background-color: var(--color);
      content: '';
      opacity: 0.15;
    }
  }
`;

const Rewards = styled(Horizontal)`
  ${transparentBlocks};
  margin-left: calc(-1 * var(--unit));
  clip-path: polygon(0 0, 100% 0, 100% 100%, var(--clip-path-delta) 100%);
  background: var(--c-black) linear-gradient(var(--bg-color), var(--bg-color));
  border: 1px solid var(--color);

  &:after {
    right: 100%;
    clip-path: polygon(
      0 0,
      0 100%,
      100% 100%,
      calc(100% - var(--clip-path-delta)) 0
    );
    transform: translateX(100%);
  }
`;
const BulletPointWrapper = styled.div`
  position: absolute;
  z-index: 1;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

type Props = {
  checked?: boolean;
  title: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  rewards?: ReactNode;
  footer?: ReactNode;
  claimable?: boolean;
  bgColor: string;
  color: Color;
  bulletPoint?: ReactNode;
};
export const MissionRow = ({
  checked,
  title,
  icon,
  description,
  status,
  rewards,
  footer,
  claimable,
  bgColor,
  color,
  bulletPoint,
}: Props) => {
  const content = (
    <Infos className={classNames({ claimable })} gap={1}>
      <SBHorizontal>
        <Horizontal>
          <Ellipsis>{title}</Ellipsis>
          {icon}
        </Horizontal>
        {status}
      </SBHorizontal>
      {footer && <div>{footer}</div>}
    </Infos>
  );

  return (
    <Root style={{ '--bg-color': bgColor, '--color': color } as CSSProperties}>
      <BulletPointWrapper>{bulletPoint}</BulletPointWrapper>
      <MissionWrapper>
        {description ? (
          <InfoWrapper title={description}>{content}</InfoWrapper>
        ) : (
          content
        )}
        {rewards && (
          <Rewards
            className={classNames({
              claimable: checked,
            })}
          >
            {rewards}
          </Rewards>
        )}
      </MissionWrapper>
    </Root>
  );
};
